import { ChevronRight } from "lucide-react";
import { useState } from "react";

import { Prose } from "@components/Prose.tsx";
import { cn } from "@lib/cn.ts";
import { deref, refName } from "@lib/content.ts";
import type { Json } from "@lib/types.ts";

const MAX_DEPTH = 12;

interface Props {
  version: string;
  schema: unknown;
  /** Property names the parent object marks required. */
  requiredNames?: string[];
  depth?: number;
  /** Ref names already expanded on this branch, to stop recursive schemas looping. */
  seen?: string[];
}

/* -------------------------------------------------------------------------- */
/* Type formatting                                                             */
/* -------------------------------------------------------------------------- */

/** `type` may be a single string or, in 3.1, an array including "null". */
function declaredTypes(schema: Json): string[] {
  const declared = schema.type;
  if (Array.isArray(declared)) return declared as string[];
  if (declared) return [String(declared)];
  return [];
}

function typeLabel(version: string, raw: unknown): string {
  const name = refName(raw);
  const schema = deref(version, raw);
  if (!schema) return "unknown";

  if (name) return name;

  if (schema.const !== undefined) return JSON.stringify(schema.const);

  const composite =
    (schema.oneOf as unknown[] | undefined) ??
    (schema.anyOf as unknown[] | undefined);
  if (composite) {
    return composite.map((s) => typeLabel(version, s)).join(" | ");
  }
  if (schema.allOf) return "object";

  const types = declaredTypes(schema);
  const nullable = types.includes("null");
  const primary = types.filter((t) => t !== "null");
  const base = primary[0] ?? (schema.properties ? "object" : "any");

  let label: string;
  if (base === "array") {
    label = `array<${schema.items ? typeLabel(version, schema.items) : "any"}>`;
  } else if (
    base === "object" &&
    schema.additionalProperties &&
    !schema.properties
  ) {
    const value =
      typeof schema.additionalProperties === "object"
        ? typeLabel(version, schema.additionalProperties)
        : "any";
    label = `map<string, ${value}>`;
  } else if (schema.format) {
    label = `${base}<${String(schema.format)}>`;
  } else {
    label = base;
  }

  return nullable ? `${label} | null` : label;
}

/** Validation facets worth surfacing next to a field. */
function constraints(schema: Json): string[] {
  const out: string[] = [];
  const push = (key: string, label = key): void => {
    if (schema[key] !== undefined) out.push(`${label} ${String(schema[key])}`);
  };

  push("minimum", "min");
  push("maximum", "max");
  push("exclusiveMinimum", "> ");
  push("exclusiveMaximum", "< ");
  push("minLength", "min length");
  push("maxLength", "max length");
  push("minItems", "min items");
  push("maxItems", "max items");
  push("multipleOf", "multiple of");
  if (schema.pattern !== undefined)
    out.push(`pattern ${String(schema.pattern)}`);
  if (schema.uniqueItems === true) out.push("unique items");
  return out;
}

function exampleValue(schema: Json): string | undefined {
  if (schema.example !== undefined) return JSON.stringify(schema.example);
  const examples = schema.examples as unknown[] | undefined;
  if (Array.isArray(examples) && examples.length > 0) {
    return JSON.stringify(examples[0]);
  }
  return undefined;
}

/* -------------------------------------------------------------------------- */
/* Enum values                                                                 */
/* -------------------------------------------------------------------------- */

function EnumValues({ schema }: { schema: Json }) {
  const values = schema.enum as unknown[] | undefined;
  if (!values?.length) return null;

  const descriptions = (schema["x-enum-descriptions"] ?? {}) as Record<
    string,
    string
  >;
  const documented = values.filter((v) => descriptions[String(v)]);

  if (documented.length === 0) {
    return (
      <div className="mt-1.5 flex flex-wrap gap-1">
        {values.map((value) => (
          <code
            key={String(value)}
            className="rounded-sm2 bg-surface-2 px-1.5 py-px text-[11.5px] text-ink"
          >
            {String(value)}
          </code>
        ))}
      </div>
    );
  }

  return (
    <dl className="mt-2 space-y-1 border-l-2 border-line pl-3">
      {values.map((value) => (
        <div
          key={String(value)}
          className="flex flex-wrap gap-x-2 text-[12.5px]"
        >
          <dt>
            <code className="rounded-sm2 bg-surface-2 px-1.5 py-px text-[11.5px] text-ink">
              {String(value)}
            </code>
          </dt>
          <dd className="text-muted">{descriptions[String(value)]}</dd>
        </div>
      ))}
    </dl>
  );
}

/* -------------------------------------------------------------------------- */
/* One field row                                                               */
/* -------------------------------------------------------------------------- */

interface FieldProps extends Props {
  name: string;
  required: boolean;
}

function Field({
  version,
  name,
  schema: raw,
  required,
  depth = 0,
  seen = [],
}: FieldProps) {
  const schema = deref(version, raw);
  const name_ = refName(raw);
  const [open, setOpen] = useState(depth < 1);

  if (!schema) {
    return (
      <div className="py-2 text-[13px] text-danger">
        <code>{name}</code> — schema could not be resolved
      </div>
    );
  }

  const cycle = Boolean(name_ && seen.includes(name_));
  const nested = hasChildren(version, schema);
  const expandable = nested && !cycle && depth < MAX_DEPTH;
  const facets = constraints(schema);
  const example = exampleValue(schema);

  return (
    <div className="border-b border-line/70 py-2.5 last:border-b-0">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        {expandable ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={`${open ? "Collapse" : "Expand"} ${name}`}
            className="-ml-1 rounded-sm2 p-0.5 text-subtle transition hover:text-ink"
          >
            <ChevronRight
              className={cn(
                "size-3.5 transition-transform",
                open && "rotate-90",
              )}
              aria-hidden
            />
          </button>
        ) : null}

        <code className="font-mono text-[13px] font-semibold text-ink">
          {name}
        </code>

        <span className="font-mono text-[12px] text-accent">
          {typeLabel(version, raw)}
        </span>

        {required ? (
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-danger">
            required
          </span>
        ) : null}

        {schema.deprecated === true ? (
          <span className="rounded-sm2 bg-warning-tint px-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-warning">
            deprecated
          </span>
        ) : null}

        {schema.readOnly === true ? (
          <span className="text-[10.5px] uppercase tracking-wide text-subtle">
            read-only
          </span>
        ) : null}
        {schema.writeOnly === true ? (
          <span className="text-[10.5px] uppercase tracking-wide text-subtle">
            write-only
          </span>
        ) : null}
      </div>

      {schema.descriptionHtml ? (
        <Prose
          size="sm"
          html={schema.descriptionHtml as string}
          className="mt-1 text-muted"
        />
      ) : null}

      {facets.length > 0 || schema.default !== undefined || example ? (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-subtle">
          {schema.default !== undefined ? (
            <span>
              default{" "}
              <code className="text-muted">
                {JSON.stringify(schema.default)}
              </code>
            </span>
          ) : null}
          {example ? (
            <span>
              example <code className="text-muted">{example}</code>
            </span>
          ) : null}
          {facets.map((facet) => (
            <span key={facet}>{facet}</span>
          ))}
        </div>
      ) : null}

      <EnumValues schema={schema} />

      {cycle ? (
        <p className="mt-1 text-[11.5px] italic text-subtle">
          Recursive reference to {name_} — expanded above.
        </p>
      ) : null}

      {expandable && open ? (
        <div className="mt-2 border-l border-line pl-3">
          <SchemaTree
            version={version}
            schema={raw}
            depth={depth + 1}
            seen={name_ ? [...seen, name_] : seen}
          />
        </div>
      ) : null}
    </div>
  );
}

function hasChildren(version: string, schema: Json): boolean {
  if (schema.properties && Object.keys(schema.properties).length > 0)
    return true;
  if (schema.allOf || schema.oneOf || schema.anyOf) return true;

  const items = schema.items ? deref(version, schema.items) : undefined;
  if (
    items &&
    (items.properties || items.allOf || items.oneOf || items.anyOf)
  ) {
    return true;
  }
  if (
    typeof schema.additionalProperties === "object" &&
    schema.additionalProperties !== null
  ) {
    const value = deref(version, schema.additionalProperties);
    if (value?.properties) return true;
  }
  return false;
}

/* -------------------------------------------------------------------------- */
/* Tree                                                                       */
/* -------------------------------------------------------------------------- */

export function SchemaTree({
  version,
  schema: raw,
  requiredNames,
  depth = 0,
  seen = [],
}: Props) {
  const schema = deref(version, raw);
  if (!schema) return null;

  if (depth > MAX_DEPTH) {
    return (
      <p className="text-[12px] italic text-subtle">
        Nesting limit reached — see the schema reference for deeper levels.
      </p>
    );
  }

  /* allOf: flatten every branch into one field list, which is how these are
     used here (a base bean plus a service-specific extension). */
  const allOf = schema.allOf as unknown[] | undefined;
  if (allOf) {
    return (
      <>
        {allOf.map((branch, index) => (
          <SchemaTree
            key={index}
            version={version}
            schema={branch}
            depth={depth}
            seen={seen}
          />
        ))}
        {schema.properties ? (
          <ObjectFields
            version={version}
            schema={schema}
            depth={depth}
            seen={seen}
          />
        ) : null}
      </>
    );
  }

  const variants =
    (schema.oneOf as unknown[] | undefined) ??
    (schema.anyOf as unknown[] | undefined);
  if (variants) {
    return (
      <Variants
        version={version}
        variants={variants}
        depth={depth}
        seen={seen}
      />
    );
  }

  if (declaredTypes(schema).includes("array") && schema.items) {
    return (
      <div>
        <p className="mb-1.5 text-[12px] text-subtle">
          Array of {typeLabel(version, schema.items)}. Each element:
        </p>
        <SchemaTree
          version={version}
          schema={schema.items}
          depth={depth}
          seen={seen}
        />
      </div>
    );
  }

  if (schema.properties) {
    return (
      <ObjectFields
        version={version}
        schema={schema}
        requiredNames={requiredNames}
        depth={depth}
        seen={seen}
      />
    );
  }

  if (
    typeof schema.additionalProperties === "object" &&
    schema.additionalProperties
  ) {
    return (
      <div>
        <p className="mb-1.5 text-[12px] text-subtle">
          Object keyed by string. Each value:
        </p>
        <SchemaTree
          version={version}
          schema={schema.additionalProperties}
          depth={depth}
          seen={seen}
        />
      </div>
    );
  }

  return null;
}

function ObjectFields({
  version,
  schema,
  requiredNames,
  depth = 0,
  seen = [],
}: Props & { schema: Json }) {
  const properties = (schema.properties ?? {}) as Json;
  const required = new Set([
    ...((schema.required as string[] | undefined) ?? []),
    ...(requiredNames ?? []),
  ]);

  return (
    <div>
      {Object.entries(properties).map(([name, propSchema]) => (
        <Field
          key={name}
          version={version}
          name={name}
          schema={propSchema}
          required={required.has(name)}
          depth={depth}
          seen={seen}
        />
      ))}
    </div>
  );
}

function Variants({
  version,
  variants,
  depth,
  seen,
}: {
  version: string;
  variants: unknown[];
  depth: number;
  seen: string[];
}) {
  const [active, setActive] = useState(0);

  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1">
        {variants.map((variant, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setActive(index)}
            className={cn(
              "rounded-sm2 border px-2 py-0.5 font-mono text-[11.5px] transition",
              index === active
                ? "border-accent bg-accent-tint text-accent"
                : "border-line text-muted hover:text-ink",
            )}
          >
            {typeLabel(version, variant)}
          </button>
        ))}
      </div>
      <SchemaTree
        version={version}
        schema={variants[active]}
        depth={depth}
        seen={seen}
      />
    </div>
  );
}

export { typeLabel };
